import { formSettingsSchema } from '@/backend/features/badging/badging-configuration/types';
import { formatHeight } from '@/backend/features/badging/badging-configuration/utils/formatHeight';
import { getAamvaDataForBarcodeGeneration } from '@/backend/features/badging/badging-configuration/utils/getAamvaDataForBarcodeGeneration';
import { RmsAccountBadgeData } from '@/backend/features/badging/badging-customizations/entities/rmsAccountBadgeEntityTypes';
import { RmsBadgeData } from '@/backend/features/badging/shared';
import { ExtendedAccountColumns, ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { getEclLabel } from '@/backend/utils/getEclLabel';
import { ImageGenerationBarcodeParams } from '@juvare/badging/backend/features/badges/types/GetTemplateDataCommon';
import { mapBarcodeAamvaDataToAamvaString } from '@juvare/badging/backend/features/barcodesAamva/utils/mapBarcodeAamvaDataToAamvaString';
import { BadgeTemplateRenderData } from '@juvare/badging/backend/types';
import { formatDate } from '@juvare/common-web-ui/utils';
import { accountEntityName } from '@juvare/contact-management/backend/features/accounts/shared';
import { eyeColorEntityName } from '@juvare/credentialing/backend/config/classifiers/entities/defineEyeColorClassifier';
import { hairColorEntityName } from '@juvare/credentialing/backend/config/classifiers/entities/defineHairColorClassifier';
import {
    EyeColorColumns,
    EyeColorData,
    HairColorColumns,
    HairColorData,
    OccupationCredentialColumns,
    OccupationCredentialData,
    occupationCredentialEntityName,
} from '@juvare/credentialing/backend/config/shared';
import { AppContext } from '@juvare/platform-app/App';
import { AppState } from '@juvare/platform-sdk-react/types';
import { Base64 } from 'js-base64';

export interface getDataForExampleImageGenerationBaseImplProps {
    badge: RmsBadgeData;
    account: ExtendedAccountColumns;
    accountBadge: RmsAccountBadgeData;
    barcode?: ImageGenerationBarcodeParams;
    isExample?: boolean; // if isExample= true, fake data will be added to account columns
    context?: AppContext<AppState>;
    profileImage?: string;
}

/**
 * base implementation of template data generation
 */
export function getDataForImageGenerationBaseImpl({
    context,
    badge,
    account,
    accountBadge,
    barcode,
    isExample = false,
    profileImage,
}: getDataForExampleImageGenerationBaseImplProps): BadgeTemplateRenderData {
    // formSettings validation
    const formSettingsValidation = formSettingsSchema.safeParse(badge.formSettings);
    if (formSettingsValidation.error) {
        throw new Error('Badge Template is incorrectly configured, form configuration does not match expected schema');
    }
    const formSettingsValidated = formSettingsValidation.data;
    const occupationId = accountBadge.options.occupationId;

    let hairColor: string | undefined;
    let eyeColor: string | undefined;
    const hairColorId: string | undefined = account.hairColor;
    const eyeColorId: string | undefined = account.eyeColor;
    if (hairColorId) {
        hairColor = context?.storage<HairColorData, HairColorColumns>(hairColorEntityName).get(hairColorId)?.data.name;
    }
    if (eyeColorId) {
        eyeColor = context?.storage<EyeColorData, EyeColorColumns>(eyeColorEntityName).get(eyeColorId)?.data.name;
    }

    // data form badge
    const issueDate = formatDate(new Date(accountBadge.issuedAt));
    const expirationDate = formatDate(new Date(accountBadge.expiresAt));

    // misc data - additional fields
    const affiliation = formSettingsValidated.affiliation;
    const dateOfBirth = account.dateOfBirth ? formatDate(account.dateOfBirth) : undefined;
    const height = formatHeight(account.heightFeet, account.heightInches);
    let organization = accountBadge.options.organizationId && context?.appState.organizations[accountBadge.options.organizationId]?.name;
    let occupation = occupationId && context?.appState.occupations[occupationId]?.name;

    let nameFirstLine = account.firstName;
    let nameSecondLine = account.lastName;
    if (formSettingsValidated.nameOrder === 'lastName') {
        nameFirstLine = account.lastName + ','; // add comma if last name goes first
        nameSecondLine = account.firstName;
    }
    if (!formSettingsValidated.showFullName) {
        nameFirstLine = account.firstName;
        nameSecondLine = '';
    }

    let ecl: string | undefined;
    const occupationEcls: string[] = account.occupationEcls;

    if (formSettingsValidated.showEcl && occupationId) {
        const eclMap = Object.fromEntries(occupationEcls.map((occEcl) => occEcl.split('_')));
        ecl = getEclLabel(eclMap[occupationId]);
    }

    const stateLicenseDefaultName = 'State License';
    let stateLicenseNumber: string | undefined;

    if (formSettingsValidated.showStateLicenseNumber && occupationId) {
        const occupationCredentialStorage = context?.storage<OccupationCredentialData, OccupationCredentialColumns>(
            occupationCredentialEntityName,
        );
        const accountStorage = context?.storage<ExtendedAccountData, ExtendedAccountColumns>(accountEntityName);

        const stateLicenseOccupationCredentialId = occupationCredentialStorage?.list({
            filter: [
                {
                    op: '=',
                    column: 'defaultName',
                    value: stateLicenseDefaultName,
                },
            ],
        })?.rows[0].id;

        if (stateLicenseOccupationCredentialId) {
            const accountData = accountStorage?.get(accountBadge.accountId).data;
            const accountOccupation = accountData?.occupations?.find((occ) => occ.occupationId === occupationId);
            const foundOccupationCredential = accountOccupation?.credentials?.[stateLicenseOccupationCredentialId];

            stateLicenseNumber = foundOccupationCredential?.licenses?.[0].licenseNumber;
        }
    }

    // used to add additional data to account columns to avoid storage calls
    if (isExample) {
        hairColor = 'Black';
        eyeColor = 'Blue';
        organization = 'Medical Reserve Corps';
        occupation = 'Social Worker';
        ecl = 'No ECL';
        stateLicenseNumber = '0000000';
    }

    const badgeTemplateRenderData: BadgeTemplateRenderData = {
        data: {
            issueDate,
            expirationDate,
            nameFirstLine,
            affiliation,
            organization,
            nameSecondLine,
            dateOfBirth,
            occupation,
            height,
            hairColor,
            eyeColor,
            ecl,
            stateLicenseNumber,
            profileImage,
        },
        settings: formSettingsValidated,
    };

    if (barcode && context) {
        const aamvaData = getAamvaDataForBarcodeGeneration({
            context: context,
            account: account,
            accountBadgeData: accountBadge,
        });
        const { ansiHeader } = mapBarcodeAamvaDataToAamvaString(aamvaData);

        badgeTemplateRenderData.barcode = {
            ansiHeader: ansiHeader,
            aamvaData: aamvaData,
            barcodeHorizontalSvg: barcode.barcodeHorizontalSvg,
            barcodeHorizontalSvgBase64: Base64.encode(barcode.barcodeHorizontalSvg),
            barcodeVerticalSvg: barcode.barcodeVerticalSvg,
            barcodeVerticalSvgBase64: Base64.encode(barcode.barcodeVerticalSvg),
        };
    }

    // custom return type - this is what's going to be passed to the handlebar templates
    return badgeTemplateRenderData;
}
